import { useMemo } from "react";
import { MenuPlacement } from "react-select";
import cn from "classnames";
import { Nullable } from "shared/lib/typeCheck.lib";
import { useTranslation } from "shared/localization/localization";
import { CarrierIcon } from "shared/ui/CarrierIcon";
import { ComponentsClassNames, CustomSelect } from "shared/ui/CustomSelect";
import { AllAirCarriersIcon } from "shared/ui/Icons";
import { createAirlineIconUrl } from "shared/utils/url.utils";
import styles from "./AirCarriersSelect.module.scss";

import { useAirCarriersQuery } from "../api/hooks/useAirCarriersQuery";

type CarrierOptionType = {
  value: string;
  label: string;
  icon?: JSX.Element;
};

type AirCarriersSelectProps = {
  value: Nullable<string>;
  onChange: (value: CarrierOptionType) => void;
  className?: string;
  isDropDownMenuRelative?: boolean;
  menuPlacement?: MenuPlacement;
  componentsClassNames?: ComponentsClassNames;
  showAllCarriersOption?: boolean;
  placeholderDataTestId?: string;
  isDisabled?: boolean;
};

export const AirCarriersSelect: RFC<AirCarriersSelectProps> = ({
  value,
  onChange,
  className,
  isDropDownMenuRelative = true,
  componentsClassNames,
  menuPlacement = "auto",
  showAllCarriersOption = false,
  placeholderDataTestId,
  isDisabled = false,
}) => {
  const { t } = useTranslation();
  const { airCarrierOptions } = useAirCarriersQuery();

  const carrierOptions: CarrierOptionType[] = useMemo(() => {
    const defaultOption: CarrierOptionType = {
      value: "",
      label: t("All carriers"),
      icon: <AllAirCarriersIcon />,
    };
    return [
      ...(showAllCarriersOption ? [defaultOption] : []),
      ...airCarrierOptions.map((carrier) => ({
        value: carrier.iataCode,
        label: carrier.name,
        icon: (
          <CarrierIcon
            src={createAirlineIconUrl(carrier.iataCode)}
            label={carrier.name}
            code={carrier.iataCode}
            className={styles.airlineImg}
          />
        ),
      })),
    ];
  }, [airCarrierOptions.length, showAllCarriersOption, t]);

  const carrierValue = useMemo(() => {
    return carrierOptions.find((option) => option.value === value) ?? null;
  }, [value, carrierOptions]);

  return (
    <CustomSelect<CarrierOptionType, false>
      className={className}
      placeholder={
        <div className={styles.placeholder}>
          <AllAirCarriersIcon />
          {t("All carriers")}
        </div>
      }
      placeholderDataTestId={placeholderDataTestId}
      isDisabled={isDisabled}
      options={carrierOptions}
      value={carrierValue}
      handleChange={onChange}
      isInputTypingEnabled
      menuPlacement={menuPlacement}
      isDropDownMenuRelative={isDropDownMenuRelative}
      selectedIndicator="checkmark"
      lazyLoadedMenuList
      componentsClassNames={{
        menuList: cn(styles.override, styles.shipmentSelectMenuList),
        ...componentsClassNames,
      }}
    />
  );
};
